import useCategoryId from '../../../../../hooks/useCategoryId';
import { useGetCategoryTreeQuery } from '../../../../../components/global/CategoryTree/apiCategoryTree';
import React, { useMemo } from 'react';
import { Row, Title } from '@ydistri/ds';
import { NavLink } from 'react-router';
import { styled } from 'styled-components';
import ConfigurationField from '../../../../../components/global/ConfigurationField/ConfigurationField';
import { ConfigurationDataType, ConfigurationFieldType } from '@ydistri/api-sdk';
import LoadingIndicator from '../../../../../components/global/LoadingIndicator/LoadingIndicator';
import { Divider } from 'antd';
import { CategoryTreeNode } from '../../../../../lib/categories/categoryTypes';
import { categoriesTillParent } from '../../../../../lib/categories/categoriesLib';
import useIsConfigurationEditable from '../../../../../hooks/useIsConfigurationEditable';
import { useTemplateOrCalculationWithValue } from '../../../../../hooks/useTemplateOrCalculationWithValue';

interface CategoryBreadcrumbsProps {
  category: CategoryTreeNode;
  last: boolean;
}

const BreadcrumbsSeparator = styled(Divider)`
  border-inline-start: 2px solid rgba(5, 5, 5, 0.2525);
`;

const BreadcrumbsLink = styled(NavLink)`
  color: black;

  &:hover {
    color: initial;
  }
`;

const CategoryBreadcrumbs: React.FC<CategoryBreadcrumbsProps> = ({ category, last }) => {
  if (last) {
    return <Title>{category.title}</Title>;
  } else {
    return (
      <>
        <BreadcrumbsLink to={`../${category.slug}`} relative="path">
          <Title $type="light">{category.title}</Title>
        </BreadcrumbsLink>
        <BreadcrumbsSeparator type="vertical" />
      </>
    );
  }
};

const CategoryConfigurationHeaderTitle: React.FC = () => {
  const calculationOrTemplate = useTemplateOrCalculationWithValue();
  const categoryId = useCategoryId();
  const { data, isFetching } = useGetCategoryTreeQuery(calculationOrTemplate.value);
  const isConfigurationEditable = useIsConfigurationEditable();

  const categoryHierarchy = useMemo(() => {
    if (!isFetching && data) {
      const category = data[categoryId];
      if (category) {
        return categoriesTillParent(category, true);
      }
    }
  }, [categoryId, data, isFetching]);

  return (
    <Row $alignItems="center">
      <ConfigurationField
        skuTypeColumn={null}
        fieldType={ConfigurationFieldType.CatEnableCategory}
        dataType={ConfigurationDataType.CheckBox}
        padding="0 1rem 0.25rem 0"
        inputWidth="4rem"
        noAdditionalSpace={true}
        autoHeight={true}
        disabled={!isConfigurationEditable}
      />
      {isFetching ? (
        <LoadingIndicator text="Loading categories..." />
      ) : (
        <Row $alignItems="center">
          {categoryHierarchy?.map(item => {
            return (
              <CategoryBreadcrumbs key={item.id} category={item} last={item.id === categoryId} />
            );
          })}
        </Row>
      )}
    </Row>
  );
};

export default CategoryConfigurationHeaderTitle;
